import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { missionApi } from '../../api/mission.api';
import { userApi } from '../../api/user.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { Spinner, Badge, Btn, Skeleton, EmptyState } from '../../components/ui/index';
import { EmojiFieldWrap } from '../../components/ui/EmojiPicker';
import FileDropZone from '../../components/ui/FileDropZone';
import AIAssistantPanel from '../../components/ai/AIAssistantPanel';
import OtherDeptParticipantPicker from '../../components/missions/OtherDeptParticipantPicker';
import TicketReferralRoadmap from '../../components/tickets/TicketReferralRoadmap';
import ReferralNoteComposer from '../../components/tickets/ReferralNoteComposer';
import TicketSmsPanel from '../../components/tickets/TicketSmsPanel';
import { timeAgo, formatDate } from '../../utils/persianNumber';
import { formatTicketSenderWithUsername } from '../../utils/ticketSender';
import { isManager } from '../../utils/roleHelpers';
import { useDraft } from '../../hooks/useDraft';
import { saveFileDraft, loadFileDraft, clearFileDraft } from '../../utils/draftStorage';
import { validateFile, getAcceptAttribute, getLimitsHint } from '../../utils/fileValidation';
import { usePaneContext, useWorkspaceNav } from '../../store/WorkspaceContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { User, Paperclip, Robot, Headset, Buildings, Clock, ArrowUUpLeft, PaperPlaneTilt, Car } from '@phosphor-icons/react';

export default function TicketProcessPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const pane = usePaneContext();
  // Inside a workspace tab there's no real :id route param (see
  // WorkspacePane.jsx) - the pane provides it directly instead.
  const id = pane?.id ?? routeId;
  // "Done, go back to the list" only means navigate('/dashboard') for a
  // normal full-page visit; from inside a tab it means close this tab and
  // let the rest of the workspace grid reflow (requirement 5).
  const backToList = () => (pane ? pane.close() : navigate('/dashboard'));
  const openTicketTab = useWorkspaceNav();
  const { user } = useAuth();
  const { addToast } = useUI();

  const [ticket, setTicket] = useState(null);
  const [thread, setThread] = useState([]);
  const [files, setFiles] = useState([]);
  const [dispatcherFiles, setDispatcherFiles] = useState([]);
  const [responseFiles, setResponseFiles] = useState([]);
  const [responseFilesByTicket, setResponseFilesByTicket] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [aiAssistLog, setAiAssistLog] = useState([]);
  const [referrals, setReferrals] = useState([]);
  // Draft lives in THIS page (not DraftTextarea + parent replyText sync).
  // That sync was the recurring bug: parent started as '' / remounted under
  // the loading skeleton and wiped what localStorage had restored.
  const replyDraftKey = id ? `ticket_reply_${id}` : '';
  const filesDraftKey = id ? `ticket_reply_files_${id}` : '';
  const [replyText, setReplyText, clearReplyDraft, replySaved] = useDraft(replyDraftKey, '', user?.username);
  const replyTaRef = useRef(null);
  const [loading, setLoading] = useState(true);
  // get_ticket_info 404s once a ticket is superseded by a newer follow-up
  // in the same thread (main_ticket_view only keeps the latest ticket_id
  // per thread) - previously that left `ticket` stuck at null with no
  // explanation, so the reply form still rendered (empty/broken) and every
  // button on it failed with a confusing generic error.
  const [ticketNotFound, setTicketNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referring, setReferring] = useState('');
  const [referNote, setReferNote] = useState('');
  const [referFiles, setReferFiles] = useState([]);
  // manager/employee_response_ticket already accept files_json as inline
  // base64 {file_name,data_url} pairs sent WITH the final submit (no
  // separate draft table for responses, unlike ticket creation) - the UI
  // just never had a picker for it and always sent files_json: [].
  // File drafts live in IndexedDB (see draftStorage.saveFileDraft) because
  // data URLs blow past localStorage quota and used to make "draft + file"
  // look broken after refresh.
  const [replyFiles, setReplyFiles] = useState([]);
  // Existing attachments of the response being edited that the employee removed.
  const [removedResponseFiles, setRemovedResponseFiles] = useState([]);
  const [filesDraftSaved, setFilesDraftSaved] = useState(false);
  const filesHydratedRef = useRef(false);
  const activeTicketRef = useRef(id);

  useEffect(() => {
    activeTicketRef.current = id;
    setChatHistory([]);
  }, [id]);

  // Mission definition - only meaningful before this ticket has been
  // referred to anyone (see addMission/queryAddMission), so this ticket is
  // still "unassigned" and the manager can send someone out in person
  // instead of just replying in text.
  const [missionLocations, setMissionLocations] = useState([]);
  // missionOwner drives (their last known location anchors the route -
  // see queryAddMission), missionCompanions are additional employees who
  // ride along but aren't the vehicle owner. missionManager is whoever is
  // responsible for submitting the mission's execution report afterward
  // (missions.manager_id - see MissionResponsePage) - defaults to the
  // vehicle owner but the dispatcher/manager defining the mission can
  // reassign it to any other participant.
  const [missionOwner, setMissionOwner] = useState('');
  const [missionCompanions, setMissionCompanions] = useState([]);
  const [missionManager, setMissionManager] = useState('');
  const [missionDestination, setMissionDestination] = useState('');
  const [definingMission, setDefiningMission] = useState(false);
  const [showMissionForm, setShowMissionForm] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [extraPeople, setExtraPeople] = useState([]);

  const manager = isManager(user?.type);
  const canReferTicket = user?.type === 'manager_employee' || user?.type === 'simple_employee';
  const canReferDown = user?.type === 'manager_employee';
  const canDefineMission = user?.type === 'manager_employee';

  const fetchData = useCallback(async ({ soft = false } = {}) => {
    const ticketId = id;
    // Soft refresh (e.g. reclaim) must NOT flip loading→true: that unmounts
    // DraftTextarea and used to cancel in-flight draft saves.
    if (!soft) setLoading(true);
    try {
      const [tRes, refRes, aiLogRes, wRes, chatRes] = await Promise.allSettled([
        ticketApi.getTicketInfo(ticketId),
        ticketApi.getReferencingList(),
        ticketApi.getAIResponseAssistLog(ticketId),
        ticketApi.getTicketWithResponse(ticketId),
        // Customer AI chat transcript (auto-resolved tickets, or the root of
        // a follow-up chain) - managers previously never loaded this, so the
        // AI answering process was invisible on the process page.
        ticketApi.getTicketChatHistory(ticketId),
      ]);
      if (ticketId !== activeTicketRef.current) return;
      if (tRes.status === 'fulfilled') {
        const d = tRes.value.data;
        setTicket(Array.isArray(d) ? d[0] : d);
        setTicketNotFound(false);
      } else {
        setTicketNotFound(true);
      }
      if (refRes.status === 'fulfilled') {
        const d = refRes.value.data;
        setReferrals(Array.isArray(d) ? d : []);
      }
      if (aiLogRes.status === 'fulfilled') {
        const d = aiLogRes.value.data;
        setAiAssistLog(Array.isArray(d) ? d : []);
      }
      // getTicketResponseList only returns THIS ticket's own responses -
      // if the customer continued the conversation (follow-up tickets
      // linked via main_ticket_id, see TicketDetailPage's "ادامه می‌دهم"),
      // earlier rounds live on different ticket_ids entirely and never
      // show up there. get_ticket_with_response walks the whole chain so
      // whoever this ticket is assigned to can see the full back-and-forth,
      // not just this one ticket's slice of it.
      if (wRes.status === 'fulfilled') {
        const d = wRes.value.data;
        const rows = Array.isArray(d) ? d : [];
        setThread(rows);
        // Response attachments live on the ticket that was answered; load
        // them for every ticket in the chain and on every refresh, so files
        // sent/edited just now (and earlier rounds') are shown.
        const tids = [...new Set([ticketId, ...rows.map((r) => r.ticket_id)].filter((x) => x != null).map(String))];
        Promise.allSettled(tids.map((tid) => ticketApi.getFileList(tid, 'response'))).then((results) => {
          if (ticketId !== activeTicketRef.current) return;
          const byTicket = {};
          tids.forEach((tid, i) => {
            const r = results[i];
            byTicket[tid] = r.status === 'fulfilled' && Array.isArray(r.value.data) ? r.value.data : [];
          });
          setResponseFilesByTicket(byTicket);
          setResponseFiles(byTicket[String(ticketId)] || []);
        });
      }
      if (chatRes.status === 'fulfilled') {
        const msgs = chatRes.value.data?.messages;
        setChatHistory(Array.isArray(msgs) ? msgs : []);
      } else {
        setChatHistory([]);
      }
    } catch {}
    if (ticketId === activeTicketRef.current && !soft) setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Paused while a submit is in flight (sendResponse/editResponse/
  // sendFollowUpMessage all flip `submitting`) so a background poll can't
  // race their own fetchData({soft:true}) call right after.
  useAutoRefresh(() => fetchData({ soft: true }), { intervalMs: 2000, paused: submitting });

  // get_ticket_with_response walks the thread straight off the `tickets`
  // table (see queryGetTicketWithResponse), so it still resolves even once
  // this ticket_id is superseded and get_ticket_info 404s (ticketNotFound).
  // Use it to jump the employee straight to whichever ticket is now current
  // in the same thread - either side sending a follow-up used to leave
  // whoever had the OLD ticket open stuck on a dead "not available" page
  // with no way back into the conversation until they reopened it from the
  // list themselves.
  useEffect(() => {
    if (!ticketNotFound || thread.length === 0) return;
    const latestTicketId = [...thread].reverse().find((r) => r.ticket_id)?.ticket_id;
    if (!latestTicketId || String(latestTicketId) === String(id)) return;
    // Inside a workspace tab the browser URL never changes for ticket
    // navigation (see WorkspacePane's own note on this) - only openTab
    // actually swaps what that pane renders, so plain navigate() here
    // would silently do nothing while still inside a tab. Reached directly
    // (a bookmark, browser back/forward, or no workspace mounted at all)
    // there's no pane to redirect within, so it must be a real navigation.
    if (pane) {
      pane.close();
      openTicketTab(`/tickets/${latestTicketId}/process`, ticket?.ticket_title || ticket?.title || 'ادامه پاسخ');
    } else {
      navigate(`/tickets/${latestTicketId}/process`, { replace: true });
    }
  }, [ticketNotFound, thread, id, pane, openTicketTab, ticket, navigate]);

  useEffect(() => {
    // Customer uploads live in tickets_files (type=ticket); dispatcher
    // assignment attachments live in dispatcher_files (type=dispatcher) -
    // previously only ticket files were fetched, so manager never saw what
    // the dispatcher attached while routing the ticket.
    Promise.allSettled([
      ticketApi.getFileList(id, 'ticket'),
      ticketApi.getFileList(id, 'dispatcher'),
      ticketApi.getFileList(id, 'response'),
    ]).then(([ticketFilesRes, dispatcherFilesRes, responseFilesRes]) => {
      setFiles(ticketFilesRes.status === 'fulfilled' && Array.isArray(ticketFilesRes.value.data)
        ? ticketFilesRes.value.data : []);
      setDispatcherFiles(dispatcherFilesRes.status === 'fulfilled' && Array.isArray(dispatcherFilesRes.value.data)
        ? dispatcherFilesRes.value.data : []);
      setResponseFiles(responseFilesRes.status === 'fulfilled' && Array.isArray(responseFilesRes.value.data)
        ? responseFilesRes.value.data : []);
    });
  }, [id]);

  // Mark ticket as seen
  useEffect(() => {
    if (manager) ticketApi.employeeManagerSeenTicket({ ticket_id: id }).catch(() => {});
    else ticketApi.employeeSimpleSeenTicket({ ticket_id: id }).catch(() => {});
  }, [id, manager]);

  const alreadyResponded = !!ticket?.responsed;
  const canEditResponse = !!ticket?.can_edit_response;

  // Restore reply file attachments from IndexedDB once per ticket/user.
  useEffect(() => {
    filesHydratedRef.current = false;
    setReplyFiles([]);
    setFilesDraftSaved(false);
    if (!filesDraftKey) return undefined;
    let cancelled = false;
    loadFileDraft(filesDraftKey, user?.username).then((files) => {
      if (cancelled) return;
      if (Array.isArray(files) && files.length) {
        setReplyFiles(files);
        setFilesDraftSaved(true);
      }
      filesHydratedRef.current = true;
    });
    return () => { cancelled = true; };
  }, [filesDraftKey, user?.username]);

  // Persist reply files whenever the list changes (after initial hydrate).
  useEffect(() => {
    if (!filesDraftKey || !filesHydratedRef.current) return undefined;
    let cancelled = false;
    saveFileDraft(filesDraftKey, replyFiles, user?.username).then((ok) => {
      if (!cancelled) setFilesDraftSaved(ok && replyFiles.length > 0);
    });
    return () => { cancelled = true; };
  }, [replyFiles, filesDraftKey, user?.username]);

  // Set right after our own submit so the box stays empty instead of the
  // prefill effect below refilling it with the text we just sent.
  const justSubmittedRef = useRef(false);

  const clearAllReplyDrafts = useCallback(() => {
    clearReplyDraft();
    setReplyFiles([]);
    setFilesDraftSaved(false);
    if (filesDraftKey) clearFileDraft(filesDraftKey, user?.username);
  }, [clearReplyDraft, filesDraftKey, user?.username]);

  // Prefill edit form from the existing response, once per response_id.
  // Tracked by ref (not "!replyText") because a leftover localStorage draft
  // under the same key - e.g. an unrelated reply the employee started
  // typing before this ticket got answered some other way - used to look
  // like "already filled" and block the real response text from ever
  // loading, leaving that stale draft sitting in the edit box. First time
  // this response_id is seen in edit mode, always load the real text.
  const prefilledResponseIdRef = useRef(null);
  useEffect(() => {
    if (justSubmittedRef.current) {
      justSubmittedRef.current = false;
      prefilledResponseIdRef.current = ticket?.response_id ?? null;
      return;
    }
    if (alreadyResponded && canEditResponse && ticket?.response_id) {
      if (prefilledResponseIdRef.current === ticket.response_id) return;
      const mine = thread[thread.length - 1];
      if (!mine) return;
      setReplyText(mine.response_text || '');
      prefilledResponseIdRef.current = ticket.response_id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.response_id, canEditResponse]);

  const handleReplyFileAdd = async (f) => {
    if (!f) return;
    const validationError = validateFile(f, user?.type, replyFiles);
    if (validationError) { addToast(validationError, 'error'); return; }
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(f);
    });
    setReplyFiles((prev) => [...prev, { file_name: f.name, data_url: dataUrl, size: f.size }]);
  };

  const removeReplyFile = (name) => setReplyFiles((prev) => prev.filter((f) => f.file_name !== name));

  const sendResponse = async () => {
    if (!replyText.trim()) { addToast('متن پاسخ الزامی است', 'error'); return; }
    setSubmitting(true);
    try {
      if (manager) {
        await ticketApi.managerResponseTicket({ ticket_id: id, response_title: '', response_description: replyText, files_json: replyFiles });
      } else {
        await ticketApi.employeeResponseTicket({ ticket_id: id, response_title: '', response_description: replyText, files_json: replyFiles });
      }
      addToast('پاسخ با موفقیت ارسال شد', 'success');
      justSubmittedRef.current = true;
      clearAllReplyDrafts();
      // Stay on the ticket so the employee can immediately send another
      // follow-up message once the first reply locks (customer seen, etc.).
      await fetchData({ soft: true });
    } catch { addToast('خطا در ارسال پاسخ', 'error'); }
    setSubmitting(false);
  };

  const editResponse = async () => {
    if (!replyText.trim()) { addToast('متن پاسخ الزامی است', 'error'); return; }
    setSubmitting(true);
    // fetchData below re-renders the whole page with fresh data; on some
    // layouts that briefly shrinks page height (e.g. a skeleton/spinner
    // elsewhere collapsing) and the browser yanks scroll back to the top.
    // Restore whatever position the employee was actually at.
    const scrollY = window.scrollY;
    try {
      await ticketApi.editResponseTicket({ ticket_id: id, response_title: '', response_description: replyText, files_json: replyFiles, remove_files: removedResponseFiles });
      setRemovedResponseFiles([]);
      addToast('پاسخ با موفقیت ویرایش شد', 'success');
      justSubmittedRef.current = true;
      clearAllReplyDrafts();
      await fetchData({ soft: true });
      // A single requestAnimationFrame wasn't enough - some of the content
      // above the reply box (referral roadmap, AI assist log) finishes
      // re-rendering a moment later and still pushes the page around after
      // that first frame, undoing the restore. Keep re-pinning the scroll
      // position for a bit so it settles wherever the employee actually was.
      const restoreUntil = Date.now() + 2000;
      const pin = () => {
        window.scrollTo(0, scrollY);
        if (Date.now() < restoreUntil) requestAnimationFrame(pin);
      };
      requestAnimationFrame(pin);
    } catch (err) {
      // editResponseTicket now returns a specific reason (e.g. the customer
      // already saw the response, or a follow-up ticket exists) instead of
      // a bare 403 - show that instead of a generic, unhelpful message so
      // the employee understands why it was rejected.
      addToast(err.response?.data?.error || 'خطا در ویرایش پاسخ', 'error');
      // Reflects the rejection immediately instead of leaving a stale
      // "still editable" form up until the next natural refresh.
      await fetchData({ soft: true });
    }
    setSubmitting(false);
  };

  // Same capability customers have with "ادامه می‌دهم": keep the conversation
  // going from the support side after the first answer is no longer editable.
  const sendFollowUpMessage = async () => {
    if (!replyText.trim()) { addToast('متن پیام الزامی است', 'error'); return; }
    setSubmitting(true);
    try {
      const res = await ticketApi.employeeFollowUpMessage({
        ticket_id: id,
        response_title: '',
        response_description: replyText,
        files_json: replyFiles,
      });
      addToast('پیام بعدی ارسال شد', 'success');
      justSubmittedRef.current = true;
      clearAllReplyDrafts();
      const newTicketId = res?.data?.ticket_id;
      if (newTicketId) {
        if (pane) pane.close();
        openTicketTab(`/tickets/${newTicketId}/process`, ticket?.ticket_title || ticket?.title || 'ادامه پاسخ');
      } else {
        await fetchData({ soft: true });
      }
    } catch { addToast('خطا در ارسال پیام بعدی', 'error'); }
    setSubmitting(false);
  };

  const referTo = async (username) => {
    setReferring(username);
    try {
      await ticketApi.employeeReferencingTicket({
        ticket_id: id,
        employee_id: username,
        note: referNote,
        files_json: referFiles,
      });
      addToast('تیکت با موفقیت ارجاع شد', 'success');
      setReferNote('');
      setReferFiles([]);
      backToList();
    } catch { addToast('خطا در ارجاع تیکت', 'error'); }
    setReferring('');
  };

  // Manager reclaims a ticket they'd referred out, instead of referring it
  // to someone else - stays on the page (rather than bouncing to the
  // dashboard like referTo) so the reply form is immediately usable.
  const reclaimTicket = async () => {
    if (!user?.username) return;
    setReferring(user.username);
    try {
      await ticketApi.employeeReferencingTicket({ ticket_id: id, employee_id: user.username });
      addToast('تیکت به شما بازگردانده شد', 'success');
      await fetchData({ soft: true });
    } catch { addToast('خطا در بازگرداندن تیکت', 'error'); }
    setReferring('');
  };

  const openMissionForm = async () => {
    setShowMissionForm(true);
    if (missionLocations.length === 0 && ticket?.company_name) {
      try {
        const res = await missionApi.getCompanyLocation(ticket.company_name);
        setMissionLocations(Array.isArray(res.data) ? res.data : []);
      } catch { addToast('خطا در دریافت شعبه‌های شرکت', 'error'); }
    }
    if (allEmployees.length === 0) {
      setEmployeesLoading(true);
      try {
        const res = await userApi.getEmployeeListView();
        setAllEmployees(Array.isArray(res.data) ? res.data : []);
      } catch { addToast('خطا در دریافت لیست کارمندان دپارتمان‌ها', 'error'); }
      setEmployeesLoading(false);
    }
  };

  const defineMission = async () => {
    if (!missionOwner) { addToast('یک کارمند برای اعزام انتخاب کنید', 'error'); return; }
    if (!missionDestination) { addToast('مقصد ماموریت را انتخاب کنید', 'error'); return; }
    setDefiningMission(true);
    try {
      await missionApi.addMission({
        ticket_id: id,
        owner_name: missionOwner,
        // Whoever is designated mission manager - defaults to the vehicle
        // owner (set below), but the dispatcher/manager can reassign it to
        // any other participant before submitting.
        manager_name: missionManager || missionOwner,
        company_name: ticket?.company_name,
        destination_description: missionDestination,
        // Vehicle owner first, plus any additional employees riding along -
        // the backend's employee_list[] already supported more than one,
        // this page just never sent more than the owner before.
        'employee_list[]': [missionOwner, ...missionCompanions.filter((u) => u !== missionOwner)],
      });
      addToast('ماموریت با موفقیت تعریف شد', 'success');
      backToList();
    } catch { addToast('خطا در تعریف ماموریت', 'error'); }
    setDefiningMission(false);
  };

  const dispatchers = referrals.filter((r) => r.is_dispatcher || r.role === 'dispatcher');
  const ownManager = referrals.filter((r) => r.is_own_manager || r.role === 'own_manager');
  const coworkers = referrals.filter((r) => !r.is_dispatcher && r.same_department && !r.is_own_manager && r.role !== 'own_manager' && r.role !== 'dispatcher');
  const otherDepts = referrals.filter((r) => r.role === 'other_manager' || r.role === 'other_dept');
  const coworkerUsernames = new Set(coworkers.map((c) => c.username));
  const missionPeople = [
    ...coworkers.map((c) => ({ username: c.username, label: c.username, department_name: c.department_name || user?.department_name || '' })),
    ...extraPeople.filter((p) => p.username && !coworkerUsernames.has(p.username)),
  ];
  const addExtraPerson = (person) => {
    if (!person?.username) return;
    setExtraPeople((prev) => (prev.some((p) => p.username === person.username) ? prev : [...prev, person]));
    setMissionCompanions((prev) => {
      if (person.username === missionOwner || prev.includes(person.username)) return prev;
      return [...prev, person.username];
    });
  };
  const removeExtraPerson = (username) => {
    setExtraPeople((prev) => prev.filter((p) => p.username !== username));
    setMissionCompanions((prev) => prev.filter((u) => u !== username));
    if (missionOwner === username) {
      setMissionOwner('');
      setMissionManager('');
    } else if (missionManager === username) {
      setMissionManager(missionOwner);
    }
  };
  // tickets.refrence_to_subord (see queryEmployeeRefrencingTicket) flips on
  // once this manager hands the ticket to a sub-employee - hide the reply
  // form only while still awaiting that employee's answer; once responsed
  // is true (their response now counts immediately, no manager confirm
  // step) let the manager see the ticket normally again.
  const referredOut = manager && !!ticket?.refrence_to_subord && !ticket?.responsed;
  // refrence_to_subord means the manager already handed this ticket to a
  // subordinate. That must NOT hide "refer back to my manager" for the
  // employee who now holds it (ticket 105 / Ali_Kianpoor → Rad_Vala).
  // Managers still cannot re-refer while they wait — they reclaim instead.
  const canReferUp = canReferTicket && (!ticket?.refrence_to_subord || user?.type === 'simple_employee');
  const showReferComposer = (
    (canReferUp && (ownManager.length > 0 || dispatchers.length > 0 || otherDepts.length > 0))
    || (canReferDown && !ticket?.refrence_to_subord && coworkers.length > 0)
  );
  const senderLabel = formatTicketSenderWithUsername(ticket);

  return (
    <div className="fade-in">
      <button onClick={() => backToList()} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 20, padding: 0 }}>
        ← بازگشت
      </button>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1,2].map((k) => <div key={k} style={{ background: '#fff', borderRadius: 16, padding: 24 }}><Skeleton h={20} w="50%" style={{ marginBottom: 10 }} /><Skeleton h={14} /><Skeleton h={14} w="70%" /></div>)}
        </div>
      ) : ticketNotFound ? (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)' }}>
          <EmptyState
            title="این تیکت دیگر در دسترس نیست"
            subtitle="احتمالاً گفتگو در یک تیکت پیگیری جدیدتر ادامه پیدا کرده. از لیست تیکت‌ها نسخه‌ی جدید را باز کنید."
          />
        </div>
      ) : (
        <div className="pj-ticket-grid">
          {/* Main column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Full interactive process across the whole reply chain (root
                ticket, all follow-up tickets, and their responses) - not
                just this ticket's own response(s). Swapped up here (was
                below the AI chat block) to trade places with the referral
                roadmap. */}
            {thread.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>تاریخچه گفتگو</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {thread.map((round, i) => {
                    const isAi = round.response_source === 'ai' || round.response_source === 'ai_edited';
                    return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {!!(round.ticket_text && String(round.ticket_text).trim()) && (
                      <div style={{ alignSelf: 'flex-start', maxWidth: '85%', background: '#EDF3FC', border: '1px solid #B7C9EC', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ fontSize: 10, color: '#29AAE1', fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><User size={11} weight="regular" /> {round.sender} · {timeAgo(round.send_date)}</div>
                        <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{round.ticket_text}</p>
                      </div>
                      )}
                      {round.response_text && (
                        <div style={{
                          alignSelf: 'flex-end', maxWidth: '85%', borderRadius: 10, padding: '10px 14px',
                          background: isAi ? '#faf5ff' : '#f0fdf4',
                          border: isAi ? '1px solid #e9d5ff' : '1px solid #bbf7d0',
                        }}>
                          <div style={{ fontSize: 10, color: isAi ? '#7c3aed' : '#15803d', fontWeight: 700, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{isAi ? <Robot size={12} weight="regular" /> : <Headset size={12} weight="regular" />} {isAi ? (round.response_source === 'ai_edited' ? 'پاسخ AI (ویرایش‌شده)' : 'پاسخ هوش‌مصنوعی') : (round.responder || 'پشتیبانی')}</span>
                            {round.response_date ? <span>· {timeAgo(round.response_date)}</span> : null}
                          </div>
                          <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{round.response_text}</p>
                          {(responseFilesByTicket[String(round.ticket_id ?? id)] || []).length > 0 && (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {(responseFilesByTicket[String(round.ticket_id ?? id)] || []).map((f, fi) => (
                                <a key={fi} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: isAi ? '#7c3aed' : '#15803d', textDecoration: 'none' }}>
                                  <Paperclip size={13} weight="regular" /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Original ticket */}
            {ticket && (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', boxShadow: '0 4px 16px rgba(35,58,122,0.08)', overflow: 'hidden' }}>
                <div className="pj-row-card" style={{ padding: '16px 24px', background: 'linear-gradient(135deg,rgba(35,58,122,0.06),transparent)', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>{ticket.ticket_title || ticket.title}</h2>
                    {senderLabel && (
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e40af', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <User size={13} weight="regular" /> ارسال‌کننده: {senderLabel}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 11, color: '#94a3b8' }}>
                      {ticket.company_name && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Buildings size={12} weight="regular" /> {ticket.company_display || ticket.company_name}</span>}
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} weight="regular" /> {timeAgo(ticket.send_date)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Badge variant={ticket.is_active ? 'blue' : 'gray'}>{ticket.is_active ? 'فعال' : 'غیرفعال'}</Badge>
                    <Badge variant="purple">#{id}</Badge>
                  </div>
                </div>
                <div style={{ padding: 24 }}>
                  <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
                  {ticket.dispatcher_description && (
                    <div style={{ marginTop: 14, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 6 }}>📝 توضیحات دیسپچر</div>
                      <p style={{ fontSize: 13, color: '#78350f', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{ticket.dispatcher_description}</p>
                    </div>
                  )}
                  {(ticket.referral_note || (ticket.referral_files && ticket.referral_files.length > 0)) && (
                    <div style={{ marginTop: 14, padding: '12px 14px', background: '#EDF3FC', border: '1px solid #B7C9EC', borderRadius: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1B3068', marginBottom: 6 }}>💬 پیام ارجاع</div>
                      {ticket.referral_note ? (
                        <p style={{ fontSize: 13, color: '#1e3a8a', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{ticket.referral_note}</p>
                      ) : null}
                      {(ticket.referral_files || []).length > 0 && (
                        <div style={{ marginTop: ticket.referral_note ? 8 : 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {ticket.referral_files.map((f, i) => (
                            <a key={i} href={BASE_MEDIA_URL + (f.file_name || f)} target="_blank" rel="noreferrer"
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #D9E4F7', color: '#233A7A', fontSize: 12, textDecoration: 'none' }}>
                              <Paperclip size={13} weight="regular" /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name || f.file_name || f}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {files.length > 0 && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>پیوست‌های مشتری</span>
                      {files.map((f, i) => (
                        <a key={i} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0', color: '#233A7A', fontSize: 12, textDecoration: 'none' }}>
                          📄 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {dispatcherFiles.length > 0 && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>پیوست‌های دیسپچر</span>
                      {dispatcherFiles.map((f, i) => (
                        <a key={i} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', color: '#b45309', fontSize: 12, textDecoration: 'none' }}>
                          <Paperclip size={13} weight="regular" /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Full AI chat transcript from the customer's AI assistant flow */}
            {chatHistory.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(139,92,246,0.15)', boxShadow: '0 4px 16px rgba(139,92,246,0.06)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 24px', background: 'linear-gradient(135deg,rgba(139,92,246,0.06),rgba(109,40,217,0.02))', borderBottom: '1px solid rgba(139,92,246,0.12)', fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>
                  <Robot size={15} weight="regular" style={{ verticalAlign: '-3px' }} /> روند پاسخ‌دهی با دستیار هوش‌مصنوعی
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
                  {chatHistory.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end' }}>
                      <div style={{
                        maxWidth: '85%', padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap',
                        background: m.role === 'user' ? '#EDF3FC' : '#faf5ff',
                        color: '#334155',
                        border: m.role === 'user' ? '1px solid #B7C9EC' : '1px solid #e9d5ff',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, color: m.role === 'user' ? '#29AAE1' : '#7c3aed' }}>
                          {m.role === 'user' ? <><User size={11} weight="regular" /> مشتری</> : <><Robot size={11} weight="regular" /> هوش‌مصنوعی</>}
                        </div>
                        {m.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referral roadmap — swapped down here (was above the
                original ticket card) to trade places with the conversation
                history block. */}
            {ticket?.referral_roadmap?.length > 0 && (
              <TicketReferralRoadmap steps={ticket.referral_roadmap} viewerUsername={user?.username} />
            )}

            {!referredOut && <TicketSmsPanel ticketId={id} />}

            {/* Reply / edit form */}
            {referredOut ? (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 16, padding: '18px 20px', color: '#b45309', fontSize: 13, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><ArrowUUpLeft size={14} weight="regular" /> این تیکت به {ticket?.refrenced_to_username ? <b>{ticket.refrenced_to_username}</b> : 'یک کارمند زیرمجموعه'} ارجاع داده شده است. پاسخ‌دهی بر عهده اوست.</span>
                <Btn variant="secondary" size="sm" onClick={reclaimTicket} loading={referring === user?.username}>
                  بازگرداندن به خودم
                </Btn>
              </div>
            ) : alreadyResponded && !canEditResponse ? (
              /* First answer is locked, but the employee can still continue
                 the conversation (same idea as the customer's follow-up). */
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', padding: 24 }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 14px', color: '#166534', fontSize: 13, marginBottom: 16 }}>
                  ✅ پاسخ قبلی ارسال شده
                  {ticket?.sender_seen ? ' (کاربر آن را مشاهده کرده است)'
                    : ticket?.ticket_is_active === false ? ' (تیکت بسته شده است)'
                    : ticket?.has_followup ? ' (ادامه گفتگو ثبت شده است)' : ''}
                  . می‌توانید پیام بعدی را ارسال کنید.
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>ارسال پیام بعدی</h3>
                  <AIAssistantPanel ticketId={id} onInsert={(data) => {
                    setReplyText(data.description || '');
                  }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <EmojiFieldWrap inputRef={replyTaRef} value={replyText} onInsert={setReplyText}>
                      <textarea
                        ref={replyTaRef}
                        value={replyText}
                        rows={5}
                        placeholder="پیام بعدی پشتیبانی را بنویسید..."
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            if (!submitting) sendFollowUpMessage();
                          }
                        }}
                        style={{
                          padding: '10px 14px', paddingBottom: 44,
                          border: '1.5px solid #e2e8f0', borderRadius: 8,
                          fontSize: 13, fontFamily: 'inherit', resize: 'vertical', direction: 'rtl',
                          outline: 'none', width: '100%', boxSizing: 'border-box',
                        }}
                      />
                    </EmojiFieldWrap>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {(replySaved || filesDraftSaved)
                        ? <span style={{ fontSize: 11, color: '#16a34a' }}>✓ پیش‌نویس ذخیره شد{replyFiles.length ? ` (${replyFiles.length} فایل)` : ''}</span>
                        : <span style={{ fontSize: 11, color: '#94a3b8' }}>پیش‌نویس خودکار</span>
                      }
                      {(replyText || replyFiles.length > 0) && (
                        <button type="button" onClick={clearAllReplyDrafts}
                          style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>
                          پاک کردن
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <FileDropZone
                      onFile={handleReplyFileAdd}
                      compact
                      hint="فایل را اینجا بکشید یا برای پیوست کردن کلیک کنید"
                      accept={getAcceptAttribute(user?.type)}
                      limitsHint={getLimitsHint(user?.type)}
                    />
                    {replyFiles.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {replyFiles.map((f) => (
                          <div key={f.file_name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <span style={{ fontSize: 16 }}>📄</span>
                            <span style={{ flex: 1, fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                            <button onClick={() => removeReplyFile(f.file_name)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <Btn onClick={sendFollowUpMessage} loading={submitting}>
                      <PaperPlaneTilt size={14} weight="regular" /> ارسال پیام بعدی
                    </Btn>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(35,58,122,0.1)', padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>{alreadyResponded ? 'ویرایش پاسخ' : 'ارسال پاسخ'}</h3>
                  <AIAssistantPanel ticketId={id} onInsert={(data) => {
                    setReplyText(data.description || '');
                  }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <EmojiFieldWrap inputRef={replyTaRef} value={replyText} onInsert={setReplyText}>
                      <textarea
                        ref={replyTaRef}
                        value={replyText}
                        rows={5}
                        placeholder="متن پاسخ کامل خود را بنویسید..."
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            if (!submitting) (alreadyResponded ? editResponse() : sendResponse());
                          }
                        }}
                        style={{
                          padding: '10px 14px', paddingBottom: 44,
                          border: '1.5px solid #e2e8f0', borderRadius: 8,
                          fontSize: 13, fontFamily: 'inherit', resize: 'vertical', direction: 'rtl',
                          outline: 'none', width: '100%', boxSizing: 'border-box',
                        }}
                      />
                    </EmojiFieldWrap>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {(replySaved || filesDraftSaved)
                        ? <span style={{ fontSize: 11, color: '#16a34a' }}>✓ پیش‌نویس ذخیره شد{replyFiles.length ? ` (${replyFiles.length} فایل)` : ''}</span>
                        : <span style={{ fontSize: 11, color: '#94a3b8' }}>پیش‌نویس خودکار</span>
                      }
                      {(replyText || replyFiles.length > 0) && (
                        <button
                          type="button"
                          onClick={clearAllReplyDrafts}
                          style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          پاک کردن
                        </button>
                      )}
                    </div>
                  </div>
                  {alreadyResponded && responseFiles.filter((f) => !removedResponseFiles.includes(f.file_name)).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>فایل‌های فعلی پاسخ</span>
                      {responseFiles.filter((f) => !removedResponseFiles.includes(f.file_name)).map((f) => (
                        <div key={f.file_name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                          <Paperclip size={14} weight="regular" />
                          <a href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer" style={{ flex: 1, fontSize: 12, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}>{f.file_name}</a>
                          <button type="button" onClick={() => setRemovedResponseFiles((prev) => [...prev, f.file_name])} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {(
                    <div>
                      <FileDropZone
                        onFile={handleReplyFileAdd}
                        compact
                        hint="فایل را اینجا بکشید یا برای پیوست کردن به پاسخ کلیک کنید"
                        accept={getAcceptAttribute(user?.type)}
                        limitsHint={getLimitsHint(user?.type)}
                      />
                      {replyFiles.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {replyFiles.map((f) => (
                            <div key={f.file_name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                              <span style={{ fontSize: 16 }}>📄</span>
                              <span style={{ flex: 1, fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                              <button onClick={() => removeReplyFile(f.file_name)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    {alreadyResponded ? (
                      <Btn onClick={editResponse} loading={submitting}>
                        💾 ذخیره ویرایش
                      </Btn>
                    ) : (
                      <Btn onClick={sendResponse} loading={submitting}>
                        <PaperPlaneTilt size={14} weight="regular" /> ارسال پاسخ
                      </Btn>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Side panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Ticket meta */}
            {ticket && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>اطلاعات تیکت</h4>
                {[
                  { label: 'شناسه', value: `#${id}` },
                  ...(senderLabel ? [{ label: 'ارسال‌کننده', value: senderLabel }] : []),
                  { label: 'تاریخ', value: ticket.send_date ? formatDate(ticket.send_date) : '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <span style={{ color: '#94a3b8' }}>{label}</span>
                    <span style={{ color: '#334155', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* AI response-assist history - every generate/improve/rewrite
                attempt made while drafting this ticket's reply */}
            {aiAssistLog.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(139,92,246,0.2)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>تاریخچه دستیار هوش‌مصنوعی</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {aiAssistLog.map((l) => (
                    <div key={l.id} style={{ borderRight: '3px solid #8b5cf6', paddingRight: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <Badge variant="purple">
                          {l.action === 'generate' ? 'تولید پاسخ' : l.action === 'improve' ? 'بهبود پاسخ' : 'بازنویسی متن'}
                        </Badge>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{timeAgo(l.created_date)}</span>
                      </div>
                      {l.employee_username && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>توسط: {l.employee_username}</div>}
                      <p style={{ fontSize: 12, color: '#475569', margin: 0, lineHeight: 1.6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {l.output_description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Refer to own department manager */}
            {showReferComposer && (
              <ReferralNoteComposer
                user={user}
                note={referNote}
                setNote={setReferNote}
                files={referFiles}
                setFiles={setReferFiles}
                addToast={addToast}
              />
            )}
            {canReferUp && ownManager.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(99,102,241,0.2)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>ارجاع به مدیر دپارتمان</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ownManager.map((m) => (
                    <button key={m.username} onClick={() => referTo(m.username)} disabled={referring === m.username}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', textAlign: 'right', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {m.username}
                        {m.department_name ? <span style={{ color: '#94a3b8', marginRight: 8 }}>({m.department_name})</span> : null}
                      </span>
                      {referring === m.username ? <Spinner size={12} /> : <span style={{ color: '#6366f1' }}>ارجاع →</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Refer to dispatcher - hidden once already referred out, to
                stop a manager from re-referring a ticket that's already
                waiting on someone else. */}
            {canReferUp && dispatchers.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(245,158,11,0.2)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>ارجاع به دیسپچر</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {dispatchers.map((d) => (
                    <Btn key={d.username} variant="secondary" loading={referring === d.username} onClick={() => referTo(d.username)}>
                      ↩ ارجاع به دیسپچر ({d.username})
                    </Btn>
                  ))}
                </div>
              </div>
            )}

            {/* Refer to sub-employee - same reasoning as above */}
            {canReferDown && !ticket?.refrence_to_subord && coworkers.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>ارجاع به کارمند زیرمجموعه</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {coworkers.map((c) => (
                    <button key={c.username} onClick={() => referTo(c.username)} disabled={referring === c.username}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', textAlign: 'right', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {c.username}
                      {referring === c.username ? <Spinner size={12} /> : <span style={{ color: '#29AAE1' }}>ارجاع →</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cross-department targets allowed by dispatcher policy */}
            {canReferUp && otherDepts.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(16,185,129,0.2)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>ارجاع به دپارتمان دیگر</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {otherDepts.map((c) => (
                    <button key={c.username} onClick={() => referTo(c.username)} disabled={referring === c.username}
                      style={{ padding: '10px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', textAlign: 'right', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: '#334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {c.department_name || c.username}
                        {c.username ? <span style={{ color: '#94a3b8', marginRight: 8 }}>({c.username})</span> : null}
                      </span>
                      {referring === c.username ? <Spinner size={12} /> : <span style={{ color: '#059669' }}>ارجاع →</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Mission definition - only makes sense while this ticket
                hasn't been referred to anyone yet (see add_mission /
                queryAddMission): sends an employee out in person to the
                ticket's company, instead of (or before) a text reply. */}
            {canDefineMission && !ticket?.refrence_to_subord && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(16,185,129,0.2)', padding: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 12 }}>تعریف ماموریت برای این تیکت</h4>
                {ticket?.has_mission ? (
                  // Already has one - add_mission has no "replace" concept,
                  // a second call would just create a second, conflicting
                  // mission row for the same ticket.
                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                    ✅ برای این تیکت قبلاً ماموریت تعریف شده است.
                  </div>
                ) : !ticket?.company_name ? (
                  // A mission's destination is a registered branch of a
                  // company (see queryAddMission's locations lookup) - a
                  // ticket whose sender has no associated company has
                  // nowhere valid to send anyone, so there's genuinely no
                  // destination to pick rather than this being a bug.
                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                    ℹ️ ارسال‌کننده این تیکت به هیچ شرکتی متصل نیست، بنابراین امکان تعریف ماموریت (که نیازمند مقصد مشخص از شعبه‌های یک شرکت است) برای آن وجود ندارد.
                  </div>
                ) : !showMissionForm ? (
                  <Btn variant="secondary" onClick={openMissionForm} style={{ width: '100%', justifyContent: 'center' }}>
                    <Car size={15} weight="regular" /> تعریف ماموریت
                  </Btn>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>راننده / صاحب وسیله نقلیه</label>
                      <select value={missionOwner} onChange={(e) => {
                          const owner = e.target.value;
                          setMissionOwner(owner);
                          setMissionCompanions((prev) => prev.filter((u) => u !== owner));
                          // Vehicle owner is the default mission manager - only
                          // reset the manager selection here if it was still
                          // following the owner (i.e. not deliberately
                          // reassigned), so switching owners doesn't clobber an
                          // intentional manager override.
                          setMissionManager((prev) => (!prev || prev === missionOwner ? owner : prev));
                        }}
                        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}>
                        <option value="">انتخاب کنید...</option>
                        {missionPeople.map((c) => (
                          <option key={c.username} value={c.username}>
                            {c.label || c.username}{c.department_name ? ` — ${c.department_name}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(missionPeople.length > 1 || extraPeople.length > 0) && (
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>همراهان (اختیاری)</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: 8 }}>
                          {missionPeople.filter((c) => c.username !== missionOwner).map((c) => (
                            <label key={c.username} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                              <input type="checkbox" checked={missionCompanions.includes(c.username)}
                                onChange={(e) => {
                                  setMissionCompanions((prev) => e.target.checked ? [...prev, c.username] : prev.filter((u) => u !== c.username));
                                  if (!e.target.checked && missionManager === c.username) setMissionManager(missionOwner);
                                }} />
                              <span>
                                {c.label || c.username}
                                {c.department_name ? <span style={{ color: '#94a3b8', marginRight: 6 }}>({c.department_name})</span> : null}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <OtherDeptParticipantPicker
                      employees={allEmployees}
                      loading={employeesLoading}
                      myDepartmentName={user?.department_name || coworkers[0]?.department_name || ''}
                      myUsername={user?.username}
                      addedUsernames={[...coworkerUsernames, ...extraPeople.map((p) => p.username), missionOwner].filter(Boolean)}
                      onAdd={addExtraPerson}
                    />
                    {extraPeople.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {extraPeople.map((p) => (
                          <span key={p.username} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: 999,
                            padding: '4px 10px', fontSize: 11, color: '#166534',
                          }}>
                            {p.label || p.username}
                            {p.department_name ? ` · ${p.department_name}` : ''}
                            <button type="button" onClick={() => removeExtraPerson(p.username)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontSize: 13, lineHeight: 1, padding: 0, fontFamily: 'inherit' }}
                              aria-label="حذف">
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {missionOwner && (
                      <div>
                        <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>مدیر ماموریت (مسئول ثبت گزارش)</label>
                        <select value={missionManager || missionOwner} onChange={(e) => setMissionManager(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}>
                          {[missionOwner, ...missionCompanions.filter((u) => u !== missionOwner)].map((u) => (
                            <option key={u} value={u}>{u}{u === missionOwner ? ' (راننده)' : ''}</option>
                          ))}
                        </select>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>پیش‌فرض راننده است، اما می‌توانید فرد دیگری از همراهان را به عنوان مدیر انتخاب کنید.</span>
                      </div>
                    )}
                    <div>
                      <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>مقصد (شعبه {ticket.company_display || ticket.company_name})</label>
                      <select value={missionDestination} onChange={(e) => setMissionDestination(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}>
                        <option value="">انتخاب کنید...</option>
                        {missionLocations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
                      </select>
                      {missionLocations.length === 0 && (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>شعبه‌ای برای این شرکت ثبت نشده است</span>
                      )}
                    </div>
                    <Btn onClick={defineMission} loading={definingMission} style={{ width: '100%', justifyContent: 'center' }}>
                      <Car size={15} weight="regular" /> ثبت ماموریت
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
